<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card>
          <v-card-title class="headline">
            General Agent Enrollment
          </v-card-title>
          <v-card-subtitle>
            Family Health Choice - Marketer Agreement
          </v-card-subtitle>

          <v-card-text>
            <v-stepper v-model="step" vertical>
              <!-- Step 1: Legal Entity Information -->
              <v-stepper-step :complete="step > 1" step="1">
                Legal Entity Information
              </v-stepper-step>
              <v-stepper-content step="1">
                <v-form ref="form1" v-model="valid1">
                  <v-text-field
                    v-model="formData.legalEntityName"
                    label="Legal Entity Name *"
                    outlined
                    dense
                    :rules="[v => !!v || 'Legal Entity Name is required']"
                  ></v-text-field>

                  <v-select
                    v-model="formData.entityType"
                    :items="entityTypes"
                    label="Entity Type (Optional)"
                    outlined
                    dense
                    hint="Optional - e.g., LLC, Corporation, Partnership"
                    persistent-hint
                    clearable
                  ></v-select>

                  <v-text-field
                    v-model="formData.businessRegistrationNumber"
                    label="Business Registration Number (Optional)"
                    outlined
                    dense
                    hint="Optional - if applicable"
                    persistent-hint
                  ></v-text-field>

                  <v-text-field
                    v-model="formData.taxId"
                    label="Federal Tax I.D. # *"
                    outlined
                    dense
                    :rules="[v => !!v || 'Federal Tax I.D. is required']"
                  ></v-text-field>

                  <v-btn color="primary" @click="validateStep(1)">
                    Continue
                  </v-btn>
                </v-form>
              </v-stepper-content>

              <!-- Step 2: Address -->
              <v-stepper-step :complete="step > 2" step="2">
                Address Information
              </v-stepper-step>
              <v-stepper-content step="2">
                <v-form ref="form2" v-model="valid2">
                  <v-autocomplete
                    v-model="selectedAddress"
                    :items="addressSuggestions"
                    :loading="searchingAddress"
                    :search-input.sync="addressSearchText"
                    item-text="description"
                    return-object
                    label="Start typing your address for suggestions *"
                    placeholder="Start typing your address..."
                    outlined
                    dense
                    clearable
                    no-filter
                    @change="selectAddressFromAutocomplete"
                    class="mb-3"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>mdi-map-marker</v-icon>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ addressSearchText ? 'Type to search addresses...' : 'Start typing your address' }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.description }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>

                  <v-divider class="my-3"></v-divider>
                  <div class="text-subtitle-2 mb-2">Or enter manually:</div>

                  <v-text-field
                    v-model="formData.address"
                    label="Address *"
                    outlined
                    dense
                    :rules="[v => !!v || 'Address is required']"
                    :readonly="addressAutoFilled"
                  >
                    <template v-slot:append v-if="addressAutoFilled">
                      <v-btn icon small @click="clearAutoFill">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-model="formData.address2"
                    label="Address Line 2 (Optional)"
                    placeholder="Apt, Suite, Unit, etc."
                    outlined
                    dense
                    hint="Optional - Apartment, suite, unit, building, floor, etc."
                    persistent-hint
                    class="mb-3"
                  ></v-text-field>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="formData.city"
                        label="City *"
                        outlined
                        dense
                        :rules="[v => !!v || 'City is required']"
                        :readonly="addressAutoFilled"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="formData.state"
                        label="State *"
                        outlined
                        dense
                        value="FL"
                        :rules="[v => !!v || 'State is required']"
                        :readonly="addressAutoFilled"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="formData.zipCode"
                        label="Zip Code *"
                        outlined
                        dense
                        :rules="[v => !!v || 'Zip Code is required']"
                        :readonly="addressAutoFilled"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-alert v-if="addressAutoFilled" type="success" dense text class="mt-2">
                    <v-icon left small>mdi-check-circle</v-icon>
                    Address auto-filled! Click X to edit manually.
                  </v-alert>

                  <v-text-field
                    v-model="formData.county"
                    label="County *"
                    outlined
                    dense
                    :rules="[v => !!v || 'County is required']"
                  ></v-text-field>

                  <v-btn text @click="step = 1">Back</v-btn>
                  <v-btn color="primary" @click="validateStep(2)">
                    Continue
                  </v-btn>
                </v-form>
              </v-stepper-content>

              <!-- Step 3: Contact Information -->
              <v-stepper-step :complete="step > 3" step="3">
                Contact Information
              </v-stepper-step>
              <v-stepper-content step="3">
                <v-form ref="form3" v-model="valid3">
                  <v-text-field
                    v-model="formData.phoneNumber"
                    label="Phone *"
                    outlined
                    dense
                    :rules="[v => !!v || 'Phone is required']"
                    @input="formatPhone"
                  ></v-text-field>

                  <v-text-field
                    v-model="formData.fax"
                    label="Fax"
                    outlined
                    dense
                    @input="formatFax"
                  ></v-text-field>

                  <v-text-field
                    v-model="formData.agentEmail"
                    label="Email *"
                    type="email"
                    outlined
                    dense
                    :rules="[
                      v => !!v || 'Email is required',
                      v => /.+@.+\..+/.test(v) || 'Email must be valid'
                    ]"
                  ></v-text-field>

                  <v-text-field
                    v-model="formData.officeManager"
                    label="Office Manager *"
                    outlined
                    dense
                    :rules="[v => !!v || 'Office Manager is required']"
                  ></v-text-field>

                  <v-text-field
                    v-model="formData.emailManager"
                    label="Email Manager *"
                    type="email"
                    outlined
                    dense
                    :rules="[
                      v => !!v || 'Email Manager is required',
                      v => /.+@.+\..+/.test(v) || 'Email must be valid'
                    ]"
                  ></v-text-field>

                  <v-btn text @click="step = 2">Back</v-btn>
                  <v-btn color="primary" @click="validateStep(3)">
                    Continue
                  </v-btn>
                </v-form>
              </v-stepper-content>

              <!-- Step 4: Licensing -->
              <v-stepper-step :complete="step > 4" step="4">
                Licensing Information
              </v-stepper-step>
              <v-stepper-content step="4">
                <v-form ref="form4" v-model="valid4">
                  <v-switch
                    v-model="formData.isLicensed"
                    label="Is General Agent Licensed to sell Health Policies?"
                  ></v-switch>

                  <v-text-field
                    v-if="formData.isLicensed"
                    v-model="formData.licenseType"
                    label="License Type"
                    outlined
                    dense
                  ></v-text-field>

                  <v-file-input
                    v-if="formData.isLicensed"
                    v-model="licenseFile"
                    label="License Document"
                    accept=".pdf,.jpg,.jpeg,.png"
                    outlined
                    dense
                    @change="handleLicenseUpload"
                  ></v-file-input>

                  <v-btn text @click="step = 3">Back</v-btn>
                  <v-btn color="primary" @click="validateStep(4)">
                    Continue
                  </v-btn>
                </v-form>
              </v-stepper-content>

              <!-- Step 5: User Account -->
              <v-stepper-step :complete="step > 5" step="5">
                User Account
              </v-stepper-step>
              <v-stepper-content step="5">
                <v-form ref="form5" v-model="valid5">
                  <v-text-field
                    v-model="formData.firstname"
                    label="First Name *"
                    outlined
                    dense
                    :rules="[v => !!v || 'First Name is required']"
                  ></v-text-field>

                  <v-text-field
                    v-model="formData.lastname"
                    label="Last Name *"
                    outlined
                    dense
                    :rules="[v => !!v || 'Last Name is required']"
                  ></v-text-field>

                  <v-text-field
                    v-model="formData.password"
                    label="Password *"
                    type="password"
                    outlined
                    dense
                    :rules="[
                      v => !!v || 'Password is required',
                      v => (v && v.length >= 8) || 'Password must be at least 8 characters'
                    ]"
                  ></v-text-field>

                  <v-text-field
                    v-model="confirmPassword"
                    label="Confirm Password *"
                    type="password"
                    outlined
                    dense
                    :rules="[
                      v => !!v || 'Please confirm password',
                      v => v === formData.password || 'Passwords do not match'
                    ]"
                  ></v-text-field>

                  <v-btn text @click="step = 4">Back</v-btn>
                  <v-btn color="primary" @click="validateStep(5)">
                    Continue
                  </v-btn>
                </v-form>
              </v-stepper-content>

              <!-- Step 6: Documents -->
              <v-stepper-step step="6">
                Required Documents
              </v-stepper-step>
              <v-stepper-content step="6">
                <v-form ref="form6" v-model="valid6">
                  <v-alert type="info" class="mb-4" dense>
                    Please complete all required documents before submitting your enrollment.
                  </v-alert>

                  <!-- W-9 Form (gerado automaticamente) -->
                  <v-card class="mb-4" outlined>
                    <v-card-title class="subtitle-1">
                      W-9 Form *
                    </v-card-title>
                    <v-card-text>
                      <p class="text-caption mb-2">This form will be automatically generated and filled with your information.</p>
                      <v-btn 
                        color="primary" 
                        small
                        @click="generateW9Form"
                        :loading="generatingW9"
                        :disabled="!submittedAgentID"
                      >
                        Generate W-9 Form
                      </v-btn>
                      <v-chip v-if="documents.w9Form" color="success" small class="ml-2">
                        <v-icon left small>mdi-check</v-icon>
                        Generated
                      </v-chip>
                      <v-chip v-else-if="submittedAgentID" color="warning" small class="ml-2">
                        Not Generated
                      </v-chip>
                      <v-chip v-else color="grey" small class="ml-2">
                        Submit enrollment first
                      </v-chip>
                    </v-card-text>
                  </v-card>

                  <!-- ACH Authorization Form (gerado automaticamente) -->
                  <v-card class="mb-4" outlined>
                    <v-card-title class="subtitle-1">
                      ACH Authorization Form *
                    </v-card-title>
                    <v-card-text>
                      <p class="text-caption mb-2">This form will be automatically generated and filled with your information.</p>
                      <v-btn 
                        color="primary" 
                        small
                        @click="generateACHForm"
                        :loading="generatingACH"
                        :disabled="!submittedAgentID"
                      >
                        Generate ACH Authorization Form
                      </v-btn>
                      <v-chip v-if="documents.achAuthorizationForm" color="success" small class="ml-2">
                        <v-icon left small>mdi-check</v-icon>
                        Generated
                      </v-chip>
                      <v-chip v-else-if="submittedAgentID" color="warning" small class="ml-2">
                        Not Generated
                      </v-chip>
                      <v-chip v-else color="grey" small class="ml-2">
                        Submit enrollment first
                      </v-chip>
                    </v-card-text>
                  </v-card>

                  <!-- Errors and Omissions Insurance -->
                  <v-card class="mb-4" outlined>
                    <v-card-title class="subtitle-1">
                      Errors and Omissions (E&O) Insurance *
                    </v-card-title>
                    <v-card-text>
                      <v-file-input
                        v-model="documentFiles.eoInsurance"
                        label="Upload E&O Insurance Document"
                        accept=".pdf,.jpg,.jpeg,.png"
                        outlined
                        dense
                        prepend-icon="mdi-file-upload"
                        @change="uploadDocument('eoInsurance', $event)"
                        :disabled="!submittedAgentID"
                      ></v-file-input>
                      <v-chip v-if="documents.eoInsurance" color="success" small>
                        <v-icon left small>mdi-check</v-icon>
                        Uploaded
                      </v-chip>
                    </v-card-text>
                  </v-card>

                  <!-- Voided Check -->
                  <v-card class="mb-4" outlined>
                    <v-card-title class="subtitle-1">
                      Voided Check *
                    </v-card-title>
                    <v-card-text>
                      <v-file-input
                        v-model="documentFiles.voidedCheck"
                        label="Upload Voided Check"
                        accept=".pdf,.jpg,.jpeg,.png"
                        outlined
                        dense
                        prepend-icon="mdi-file-upload"
                        @change="uploadDocument('voidedCheck', $event)"
                        :disabled="!submittedAgentID"
                      ></v-file-input>
                      <v-chip v-if="documents.voidedCheck" color="success" small>
                        <v-icon left small>mdi-check</v-icon>
                        Uploaded
                      </v-chip>
                    </v-card-text>
                  </v-card>

                  <!-- Driver License -->
                  <v-card class="mb-4" outlined>
                    <v-card-title class="subtitle-1">
                      Driver License *
                    </v-card-title>
                    <v-card-text>
                      <v-file-input
                        v-model="documentFiles.driverLicense"
                        label="Upload Driver License"
                        accept=".pdf,.jpg,.jpeg,.png"
                        outlined
                        dense
                        prepend-icon="mdi-file-upload"
                        @change="uploadDocument('driverLicense', $event)"
                        :disabled="!submittedAgentID"
                      ></v-file-input>
                      <v-chip v-if="documents.driverLicense" color="success" small>
                        <v-icon left small>mdi-check</v-icon>
                        Uploaded
                      </v-chip>
                    </v-card-text>
                  </v-card>

                  <!-- Insurance License (Opcional) -->
                  <v-card class="mb-4" outlined>
                    <v-card-title class="subtitle-1">
                      Insurance License (Optional)
                    </v-card-title>
                    <v-card-text>
                      <v-file-input
                        v-model="documentFiles.insuranceLicense"
                        label="Upload Insurance License"
                        accept=".pdf,.jpg,.jpeg,.png"
                        outlined
                        dense
                        prepend-icon="mdi-file-upload"
                        @change="uploadDocument('insuranceLicense', $event)"
                        :disabled="!submittedAgentID"
                      ></v-file-input>
                      <v-chip v-if="documents.insuranceLicense" color="success" small>
                        <v-icon left small>mdi-check</v-icon>
                        Uploaded
                      </v-chip>
                    </v-card-text>
                  </v-card>

                  <v-btn text @click="step = 5">Back</v-btn>
                  <v-btn
                    color="success"
                    :loading="loading"
                    :disabled="!canSubmitDocuments"
                    @click="submitEnrollment"
                  >
                    Submit Enrollment
                  </v-btn>
                </v-form>
              </v-stepper-content>
            </v-stepper>
          </v-card-text>
        </v-card>

        <!-- Success Dialog -->
        <v-dialog v-model="successDialog" max-width="500">
          <v-card>
            <v-card-title class="headline success--text">
              Enrollment Submitted Successfully!
            </v-card-title>
            <v-card-text>
              <p>Your General Agent enrollment has been submitted and is pending approval.</p>
              <p><strong>Agent ID:</strong> {{ submittedAgentID }}</p>
              <p>You will receive an email notification once your enrollment is approved.</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="successDialog = false">
                Continue to Documents
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Error Snackbar -->
        <v-snackbar v-model="errorSnackbar" color="error" :timeout="5000">
          {{ errorMessage }}
        </v-snackbar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "GeneralAgentEnrollment",
  components: {},
  data() {
    return {
      step: 1,
      valid1: false,
      valid2: false,
      valid3: false,
      valid4: false,
      valid5: false,
      valid6: false,
      loading: false,
      confirmPassword: "",
      licenseFile: null,
      successDialog: false,
      submittedAgentID: "",
      errorSnackbar: false,
      errorMessage: "",
      // Documents
      documents: {
        w9Form: null,
        achAuthorizationForm: null,
        eoInsurance: null,
        voidedCheck: null,
        driverLicense: null,
        insuranceLicense: null
      },
      documentFiles: {
        eoInsurance: null,
        voidedCheck: null,
        driverLicense: null,
        insuranceLicense: null
      },
      generatingW9: false,
      generatingACH: false,
      // Address autocomplete
      addressSearchText: '',
      selectedAddress: null,
      addressSuggestions: [],
      searchingAddress: false,
      addressAutoFilled: false,
      addressSearchTimeout: null,
      entityTypes: [
        "LLC",
        "Corporation",
        "Partnership",
        "Sole Proprietorship",
        "Other",
      ],
      formData: {
        legalEntityName: "",
        entityType: "",
        businessRegistrationNumber: "",
        taxId: "",
        address: "",
        address2: "",
        city: "",
        state: "FL",
        zipCode: "",
        county: "",
        phoneNumber: "",
        fax: "",
        agentEmail: "",
        officeManager: "",
        emailManager: "",
        isLicensed: false,
        licenseType: "",
        licenseDocument: "",
        firstname: "",
        lastname: "",
        password: "",
      },
    };
  },
  methods: {
    validateStep(stepNum) {
      const formRef = `form${stepNum}`;
      if (this.$refs[formRef].validate()) {
        this.step = stepNum + 1;
      }
    },
    formatPhone() {
      let x = this.formData.phoneNumber
        .replace(/\D/g, "")
        .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      this.formData.phoneNumber = !x[2]
        ? x[1]
        : "(" + x[1] + ") " + x[2] + (x[3] ? "-" + x[3] : "");
    },
    formatFax() {
      let x = this.formData.fax
        .replace(/\D/g, "")
        .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      this.formData.fax = !x[2]
        ? x[1]
        : "(" + x[1] + ") " + x[2] + (x[3] ? "-" + x[3] : "");
    },
    handleLicenseUpload(file) {
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.formData.licenseDocument = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    async submitEnrollment() {
      // Se ainda não submeteu o enrollment básico, submeter primeiro
      if (!this.submittedAgentID) {
        if (!this.$refs.form5.validate()) {
          this.step = 5;
          return;
        }

        this.loading = true;
        try {
          const response = await axios.post(
            "/api/agent-enrollment/general-agent/enroll",
            this.formData
          );

          this.submittedAgentID = response.data.agentID;
          this.step = 6; // Avançar para step de documentos
          this.successDialog = true;
          this.loading = false;
        } catch (error) {
          this.loading = false;
          this.errorMessage =
            error.response?.data?.message ||
            "Error submitting enrollment. Please try again.";
          this.errorSnackbar = true;
        }
        return;
      }

      // Se já submeteu, validar documentos
      if (!this.canSubmitDocuments) {
        this.errorMessage = "Please complete all required documents before submitting.";
        this.errorSnackbar = true;
        return;
      }

      // Todos os documentos estão completos - enrollment finalizado
      this.successDialog = true;
    },
    goToLogin() {
      this.$router.push("/login");
    },
    async selectAddressFromAutocomplete(selectedItem) {
      if (!selectedItem) return;
      
      const placeId = selectedItem.place_id;
      const featureData = selectedItem.feature_data;
      
      try {
        this.searchingAddress = true;
        
        // Use Mapbox API to get address details
        const response = await axios.get('/api/geocoding/place-details', {
          params: {
            placeId: placeId,
            featureData: featureData
          }
        });
        
        const addressComponents = response.data.address_components;
        
        // Parse address components
        let street = '';
        let city = '';
        let state = '';
        let zipCode = '';
        
        addressComponents.forEach(component => {
          const types = component.types;
          
          if (types.includes('street_number')) {
            street = component.long_name;
          }
          if (types.includes('route')) {
            street += (street ? ' ' : '') + component.long_name;
          }
          if (types.includes('locality')) {
            city = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            state = component.short_name;
          }
          if (types.includes('postal_code')) {
            zipCode = component.long_name;
          }
        });
        
        // Auto-fill form fields
        this.formData.address = street;
        this.formData.city = city;
        this.formData.state = state || 'FL';
        this.formData.zipCode = zipCode;
        this.addressAutoFilled = true;
        
      } catch (error) {
        console.error('Error getting address details:', error);
      } finally {
        this.searchingAddress = false;
      }
    },
    clearAutoFill() {
      this.addressAutoFilled = false;
      this.selectedAddress = null;
      this.addressSearchText = '';
    },
    async searchAddresses(query) {
      if (!query || query.length < 3) return;
      
      try {
        this.searchingAddress = true;
        
        // Call Google Places Autocomplete API through our backend
        const response = await axios.get('/api/geocoding/autocomplete', {
          params: {
            input: query,
            components: 'country:us' // Restrict to US addresses
          }
        });
        
        this.addressSuggestions = response.data.predictions || [];
        
      } catch (error) {
        console.error('Error searching addresses:', error);
        this.addressSuggestions = [];
      } finally {
        this.searchingAddress = false;
      }
    },
    async generateW9Form() {
      if (!this.submittedAgentID) {
        this.errorMessage = "Please complete enrollment first";
        this.errorSnackbar = true;
        return;
      }
      
      this.generatingW9 = true;
      try {
        const response = await axios.post(`/api/agent-documents/generate-w9/${this.submittedAgentID}`);
        this.documents.w9Form = response.data.documentUrl;
        this.errorMessage = "W-9 form generated successfully";
        this.errorSnackbar = true;
        setTimeout(() => {
          this.errorSnackbar = false;
        }, 3000);
      } catch (error) {
        this.errorMessage = error.response?.data?.message || "Error generating W-9 form";
        this.errorSnackbar = true;
      } finally {
        this.generatingW9 = false;
      }
    },
    
    async generateACHForm() {
      if (!this.submittedAgentID) {
        this.errorMessage = "Please complete enrollment first";
        this.errorSnackbar = true;
        return;
      }
      
      this.generatingACH = true;
      try {
        const response = await axios.post(`/api/agent-documents/generate-ach/${this.submittedAgentID}`);
        this.documents.achAuthorizationForm = response.data.documentUrl;
        this.errorMessage = "ACH Authorization Form generated successfully";
        this.errorSnackbar = true;
        setTimeout(() => {
          this.errorSnackbar = false;
        }, 3000);
      } catch (error) {
        this.errorMessage = error.response?.data?.message || "Error generating ACH form";
        this.errorSnackbar = true;
      } finally {
        this.generatingACH = false;
      }
    },
    
    async uploadDocument(documentType, file) {
      if (!file || !this.submittedAgentID) {
        return;
      }
      
      const formData = new FormData();
      formData.append('document', file);
      formData.append('documentType', documentType);
      
      try {
        const response = await axios.post(
          `/api/agent-documents/upload/${this.submittedAgentID}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        this.documents[documentType] = response.data.documentUrl;
        this.errorMessage = `${documentType} uploaded successfully`;
        this.errorSnackbar = true;
        setTimeout(() => {
          this.errorSnackbar = false;
        }, 3000);
      } catch (error) {
        this.errorMessage = error.response?.data?.message || `Error uploading ${documentType}`;
        this.errorSnackbar = true;
      }
    },
  },
  computed: {
    canSubmitDocuments() {
      // Validar que documentos obrigatórios foram enviados
      return !!(
        this.documents.w9Form &&
        this.documents.achAuthorizationForm &&
        this.documents.eoInsurance &&
        this.documents.voidedCheck &&
        this.documents.driverLicense
      );
    },
  },
  watch: {
    addressSearchText(newValue) {
      if (!newValue || newValue.length < 3) {
        this.addressSuggestions = [];
        return;
      }
      
      // Debounce the search
      clearTimeout(this.addressSearchTimeout);
      this.addressSearchTimeout = setTimeout(() => {
        this.searchAddresses(newValue);
      }, 300);
    },
  },
};
</script>

<style scoped>
.v-stepper__header {
  background-color: #f5f5f5;
}
</style>

